import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Compact style: on mobile the slideshow takes the height of the photo being shown
    if (this.classList.contains('media-gallery--compact')) {
      this.addEventListener(SlideshowSelectEvent.eventName, this.#syncSlideHeight, { signal });
      window.addEventListener('resize', this.#syncSlideHeight, { signal });
      requestAnimationFrame(this.#syncSlideHeight);
    }

    // Full-screen lightbox: load it once the page is idle, or as soon as the shopper touches the gallery
    if (this.dataset.lightbox === 'photoswipe') {
      this.addEventListener('click', this.#handleLightboxClick, { signal });
      this.addEventListener('pointerdown', () => this.#loadLightbox(), { once: true, signal });

      const preload = () => ('requestIdleCallback' in window ? requestIdleCallback : setTimeout)(() => this.#loadLightbox());
      if (document.readyState === 'complete') preload();
      else window.addEventListener('load', preload, { once: true, signal });
    }
  }

  #controller = new AbortController();

  /** @type {Promise<typeof import('./product-lightbox')> | null} */
  #lightboxModule = null;

  #loadLightbox() {
    const { lightboxModule, lightboxCss, lightboxJs, lightboxUi } = this.dataset;
    if (!lightboxModule || !lightboxCss || !lightboxJs || !lightboxUi) return null;

    this.#lightboxModule ??= import(lightboxModule).then(async (lightbox) => {
      await lightbox.loadLightboxAssets({ css: lightboxCss, js: lightboxJs, ui: lightboxUi });
      return lightbox;
    });
    this.#lightboxModule.catch(() => (this.#lightboxModule = null));

    return this.#lightboxModule;
  }

  /**
   * Opens the lightbox on the clicked photo (grid on desktop, slideshow on mobile).
   * @param {MouseEvent} event
   */
  #handleLightboxClick = async (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest('button, a, deferred-media, video, model-viewer, slideshow-controls')) return;

    const item = target.closest('.media-gallery__grid > li, slideshow-slide');
    if (!item || !this.contains(item)) return;

    const items = item.matches('slideshow-slide')
      ? [...this.querySelectorAll('slideshow-slide')]
      : [...(item.parentElement?.children ?? [])];
    const mediaIndex = items.indexOf(item);

    /** @type {Array<{ type: string, src?: string, width?: number, height?: number }>} */
    const media = JSON.parse(this.querySelector('script[data-lightbox-photos]')?.textContent || '[]');
    if (media[mediaIndex]?.type !== 'image') return;

    event.preventDefault();

    const photos = media.filter((entry) => entry.type === 'image');
    const photoIndex = photos.findIndex((photo) => photo.src === media[mediaIndex].src);

    const lightbox = await this.#loadLightbox();
    lightbox?.openLightbox({
      photos: /** @type {import('./product-lightbox').LightboxPhoto[]} */ (photos),
      index: photoIndex,
      onClose: (index) => {
        // Leave the mobile slideshow on the last photo viewed in the lightbox
        const slideIndex = media.findIndex((entry) => entry.src === photos[index]?.src);
        if (slideIndex >= 0) this.slideshow?.select(slideIndex, undefined, { animate: false });
      },
    });
  };

  /**
   * @param {Event} [event]
   */
  #syncSlideHeight = (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.style.removeProperty('--compact-slide-height');
      return;
    }

    const slide =
      (event instanceof SlideshowSelectEvent && event.detail.slide) ||
      this.querySelector('slideshow-slide[aria-hidden="false"]') ||
      this.querySelector('slideshow-slide');
    const height = slide?.getBoundingClientRect().height;

    if (height) this.style.setProperty('--compact-slide-height', `${height}px`);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
